import { HeatmapItem } from '@customer/store/apis/heatmapsApi';
import L, { Map } from 'leaflet';
import { LayerGroup, LayersControl, useMapEvents } from 'react-leaflet';
import { useEffect, useMemo, useRef } from 'react';
import { HeatmapLayer } from '../heatmap-layer/HeatmapLayer';
import { LatLngIntensity } from '../visualisation/HeatmapCanvas';
import { useTranslation } from 'react-i18next';

interface HeatmapLayerProps {
  items: HeatmapItem[];
  cellWidth: number | null;
  onMapMoveEnd: (map: Map) => void;
}

export const ReactHeatmapLayer = ({ items, cellWidth, onMapMoveEnd }: HeatmapLayerProps) => {
  const { t } = useTranslation();

  const layerGroupRef = useRef<L.LayerGroup>(null);

  const heatmapPoints = useMemo<LatLngIntensity[]>(
    () => items.map(({ lat, lon, int }: HeatmapItem) => [lat, lon, int]),
    [items],
  );

  const map: Map = useMapEvents({
    // Note: a map resize event also triggers the 'moveend' event.
    // 'moveend' should trigger at the end of every change to the map viewport, including 'zoomend' and 'dragend'.
    moveend: () => {
      onMapMoveEnd(map);
    },
  });

  useEffect(() => {
    const ref = layerGroupRef.current;

    if (ref && heatmapPoints.length && cellWidth) {
      const heat: HeatmapLayer = new HeatmapLayer(heatmapPoints, cellWidth);
      ref.addLayer(heat);
    }

    return () => {
      ref?.clearLayers();
    };
  }, [heatmapPoints, cellWidth]);

  return (
    <LayersControl.Overlay name={t('Heatmap')} checked={true}>
      <LayerGroup ref={layerGroupRef} />
    </LayersControl.Overlay>
  );
};

export default ReactHeatmapLayer;
